/** tm/components/BundleLinkModal.tsx — 자료수집 링크 보내기(이력 기반 묶음 선택)
 *  case1: 이미 만든 묶음이 있으면 → 같은 링크 재사용 / 다시 묶기
 *  case2: 없으면 → 함께 묶을 건 체크 선택 → 묶음 링크 생성 / 이 건만 단독
 *  공용: 자료수집 DetailScreen(.jsx) + TM CollectStepConfigPanel + CollectDetailModal */
import { useState, useEffect } from 'react';

interface BProj { id: string; label: string; itemCount: number; reason?: string; companyName?: string }
interface DupeGroup { key: string; label: string; labels?: string[]; rep: string; itemIds: string[]; projectCount: number; kind?: 'file' | 'answer' }
interface BBundle { token: string; projectIds: string[]; labels: string[]; createdAt: string; count: number }
interface BInfo { company: { id: string; name: string } | null; bundles: BBundle[]; projects: BProj[] }

interface Props {
  projectId: string;
  individualLink?: string;          // 단독 링크(없으면 /portal/:projectId)
  onClose: () => void;
  onToast: (msg: string, type?: string) => void;
}

const ORIGIN = () => window.location.origin || 'https://roum-partners.com';
const AUTH = () => 'Bearer ' + (localStorage.getItem('token') || '');

async function copyText(text: string) {
  try { await navigator.clipboard.writeText(text); return; } catch (e) { /* fallthrough */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-9999px';
    document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    return;
  } catch (e) { /* fallthrough */ }
  window.prompt('링크를 복사하세요:', text);
}
function fmtDate(s: string): string {
  try { const d = new Date(s); return (d.getMonth() + 1) + '/' + d.getDate(); } catch (e) { return ''; }
}

export default function BundleLinkModal({ projectId, individualLink, onClose, onToast }: Props) {
  const [info, setInfo] = useState<BInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'history' | 'select' | 'dupes'>('history');
  const [dupes, setDupes] = useState<DupeGroup[]>([]);
  const [dupeSel, setDupeSel] = useState<Record<string, boolean>>({});
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  const indiv = individualLink || (ORIGIN() + '/portal/' + projectId);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch('/api/portal/bundle-info/' + projectId, { headers: { Authorization: AUTH() } });
        const j = await r.json();
        const data: BInfo = (j && j.data) || { company: null, bundles: [], projects: [] };
        if (!alive) return;
        // 묶을 게 전혀 없으면(다른 건 없고 이력도 없음) 바로 개별 복사 후 닫기 — 빈 모달 방지
        if ((data.bundles || []).length === 0 && (data.projects || []).length <= 1) {
          await copyText(indiv); onToast('링크가 복사되었습니다', 'success'); onClose(); return;
        }
        const init: Record<string, boolean> = {};
        (data.projects || []).forEach(p => { init[p.id] = (p.id === projectId); });
        setInfo(data); setSel(init);
        setMode((data.bundles || []).length > 0 ? 'history' : 'select');
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        await copyText(indiv); onToast('링크가 복사되었습니다', 'success'); onClose();
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  async function copyBundle(token: string) {
    await copyText(ORIGIN() + '/portal/co/' + token);
    onToast('묶음 링크가 복사되었습니다 — 고객에게 보내세요', 'success'); onClose();
  }
  async function doCreate(ids: string[], commonGroups: DupeGroup[]) {
    setBusy(true);
    try {
      const r = await fetch('/api/portal/bundle', { method: 'POST', headers: { Authorization: AUTH(), 'Content-Type': 'application/json' }, body: JSON.stringify({ projectIds: ids, commonGroups }) });
      const j = await r.json();
      if (j && j.success && j.data && j.data.token) { await copyBundle(j.data.token); }
      else { onToast((j && j.error) || '묶음 생성 실패', 'error'); setBusy(false); }
    } catch (e) { onToast(((e as Error) && (e as Error).message) || '묶음 생성 실패', 'error'); setBusy(false); }
  }
  async function createBundle() {
    const ids = Object.keys(sel).filter(k => sel[k]);
    if (ids.length < 2) { onToast('2건 이상 선택하세요 (단독은 아래 버튼)', 'error'); return; }
    setBusy(true);
    try {
      // 중복 요청자료 감지 — 있으면 공통 섹션 확인 단계로 (사용자 확정: 물어보고 묶기)
      const r = await fetch('/api/portal/bundle-dupes', { method: 'POST', headers: { Authorization: AUTH(), 'Content-Type': 'application/json' }, body: JSON.stringify({ projectIds: ids }) });
      const j = await r.json();
      const groups: DupeGroup[] = (j && j.success && j.data && j.data.groups) || [];
      if (groups.length > 0) {
        const init: Record<string, boolean> = {};
        groups.forEach(g => { init[g.key] = false; });   // 처음엔 아무것도 안 묶음 — 지도사가 고른 것만 「공통」(2026-10-27 「사전 고지 없이 멋대로」)
        setDupes(groups); setDupeSel(init); setMode('dupes'); setBusy(false); return;
      }
      await doCreate(ids, []);
    } catch (e) { await doCreate(ids, []); } // 감지 실패해도 묶음 생성은 진행 (기능 저하 없음)
  }
  async function confirmDupes() {
    const ids = Object.keys(sel).filter(k => sel[k]);
    await doCreate(ids, dupes.filter(g => dupeSel[g.key]));
  }
  async function copyIndiv() {
    await copyText(indiv); onToast('이 건만 단독 링크가 복사되었습니다', 'success'); onClose();
  }

  if (loading) return null;

  const projects = (info && info.projects) || [];
  const bundles = (info && info.bundles) || [];
  const selCount = Object.keys(sel).filter(k => sel[k]).length;
  const showHistory = mode === 'history' && bundles.length > 0;

  return (
    <div onClick={onClose} style={ov}>
      <div onClick={e => e.stopPropagation()} style={card}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: '#0f172a' }}>📎 자료수집 링크 보내기</div>

        {mode === 'dupes' ? (
          <>
            <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 3, lineHeight: 1.6 }}>
              여러 건에 <b>같은 이름 자료</b>가 있어요. 한 번만 받을 자료를 <b>골라 주세요</b> — 고르지 않으면 건마다 따로 받아요.
            </div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 12 }}>
              체크한 자료는 각 섹션에서 빠지고 「공통」 섹션에서 한 번만 받아요 — 받은 파일 · 답변은 묶인 모든 건에 저절로 들어가요.
              {dupes.some(g => g.kind === 'answer') && <> 건마다 답이 다를 답변은 체크를 빼 주세요.</>}
            </div>
            <div style={{ maxHeight: 230, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {dupes.map(g => (
                <label key={g.key} style={{ ...chk, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!dupeSel[g.key]} onChange={() => setDupeSel(s => ({ ...s, [g.key]: !s[g.key] }))} style={{ width: 16, height: 16, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 12.5, color: '#1e293b' }}>
                    {/* 파일 · 답변 구분(2026-10-26 지도사 「텍스트 입력 답변받는 것도 여기 보여줘」) */}
                    <span style={{ display: 'inline-block', minWidth: 30, marginRight: 6, padding: '1px 6px', borderRadius: 4, fontSize: 11, fontWeight: 700, textAlign: 'center', background: g.kind === 'answer' ? '#EEF3F8' : '#F1F5F9', color: g.kind === 'answer' ? '#1B3A5C' : '#475569' }}>{g.kind === 'answer' ? '답변' : '파일'}</span>
                    {g.label} <span style={{ color: '#94a3b8' }}>— {g.projectCount}개 건에서 요청</span>
                    {g.labels && g.labels.length > 1 && (
                      <span style={{ display: 'block', fontSize: 11, color: '#B45309', marginTop: 2 }}>
                        ⚠ 표기가 달라요: {g.labels.join(' · ')} — 같은 서류가 맞는지 확인 후 체크
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={confirmDupes} disabled={busy}
                style={{ ...btnP, flex: 1, textAlign: 'center', opacity: busy ? 0.5 : 1 }}>
                {busy ? '생성 중…' : Object.values(dupeSel).some(Boolean) ? `${Object.values(dupeSel).filter(Boolean).length}개는 공통으로 · 묶음 링크 만들기` : '공통 없이 묶음 링크 만들기'}
              </button>
              <button onClick={() => { setMode('select'); }} style={btnG}>뒤로</button>
            </div>
          </>
        ) : showHistory ? (
          <>
            <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 12, lineHeight: 1.6 }}>
              이미 만든 묶음이 있어요. 고객에게는 <b>처음 받은 것과 같은 링크</b>를 다시 보내세요.
            </div>
            {bundles.map(b => (
              <div key={b.token} style={row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1e293b' }}>
                    {b.labels.length ? b.labels.join(' · ') : (b.count + '건')}
                    <span style={{ color: '#94a3b8', fontWeight: 400 }}> ({b.count}건)</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmtDate(b.createdAt)} 생성</div>
                </div>
                <button onClick={() => copyBundle(b.token)} style={btnP}>이 묶음 링크 복사</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={() => setMode('select')} style={btnG}>다시 묶기 — 구성 바꾸기</button>
              <button onClick={copyIndiv} style={btnG}>이 건만 단독</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 3, lineHeight: 1.6 }}>
              다른 자료수집 건과 <b>묶어서 한 링크</b>로 보낼까요?
            </div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 12 }}>
              고객이 한 화면에서 다 제출합니다. 덜 냈으면 같은 링크를 다시 보내면 돼요.
            </div>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>함께 묶을 건 선택</div>
            <div style={{ maxHeight: 230, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {projects.map(p => {
                const cur = p.id === projectId;
                return (
                  <label key={p.id} style={{ ...chk, cursor: cur ? 'default' : 'pointer' }}>
                    <input type="checkbox" checked={!!sel[p.id]} disabled={cur}
                      onChange={() => setSel(s => ({ ...s, [p.id]: !s[p.id] }))} style={{ width: 16, height: 16, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 12.5, color: '#1e293b', minWidth: 0 }}>
                      {p.label} <span style={{ color: '#94a3b8' }}>({p.itemCount}개 항목)</span>
                      {cur && <span style={{ color: '#2563eb', fontSize: 10.5, marginLeft: 6 }}>현재 건</span>}
                      {!cur && p.reason && p.reason !== '같은 업체' && <span style={{ color: '#B45309', background: '#FEF3C7', fontSize: 10, fontWeight: 700, marginLeft: 6, padding: '1px 6px', borderRadius: 8 }}>{p.reason}</span>}
                    </span>
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={createBundle} disabled={busy || selCount < 2}
                style={{ ...btnP, flex: 1, textAlign: 'center', opacity: (busy || selCount < 2) ? 0.5 : 1, cursor: (busy || selCount < 2) ? 'default' : 'pointer' }}>
                {busy ? '생성 중…' : (selCount >= 2 ? '묶음 링크 생성 (' + selCount + '건)' : '묶음 링크 생성')}
              </button>
              <button onClick={copyIndiv} style={btnG}>이 건만 단독</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const ov: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: '20px 22px', width: 440, maxWidth: '100%', boxShadow: '0 20px 50px rgba(0,0,0,.25)' };
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 8 };
const chk: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 9 };
const btnP: React.CSSProperties = { padding: '9px 14px', borderRadius: 9, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const btnG: React.CSSProperties = { padding: '9px 14px', borderRadius: 9, border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' };
